/**
 * Per-location tax-rates API wrapper (v2 — Project Independence, DNKADM-0051 U3).
 *
 * Tax rates are nested under an owned location (handoff §5.2): a tenant's "State" /
 * "City" tax, entered once per location. **Tax is always exclusive** (added on top —
 * there is NO inclusive mode / toggle) and **all-or-none** (a taxable service applies
 * EVERY active rate at the booking's location — there is no per-service rate
 * selection; taxability is a service-level flag, §5.5). Unique on `(location, name)`.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.2 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/locations/{locationId}/tax-rates?status=active|inactive|all`.
 *  - `POST   /v2/admin/locations/{locationId}/tax-rates` ← {@link TaxRateInput}.
 *  - `PUT    /v2/admin/locations/{locationId}/tax-rates/{id}` ← {@link TaxRateInput}.
 *  - `POST   /v2/admin/locations/{locationId}/tax-rates/{id}/deactivate` |
 *    `/reactivate` (soft-delete / reactivate, §4.3).
 *
 * **`v2_tax_rate` fields:** `id`, `locationId`, `name` ("State"/"City"),
 * `ratePercent` (DECIMAL(5,2), e.g. `6.00` — a percentage 0–100), `description`,
 * `taxId`, `taxAgency`, `isActive`. Every field is parsed DEFENSIVELY (mirrors
 * `locations.ts` / `tenantSettings.ts`); a bad entry is dropped, never a throw.
 */
import { request } from './client';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin owned-locations base path (bearer surface). */
const LOCATIONS_PATH = '/v2/admin/locations';

/** The tax-rates collection path for an owned location id. */
function taxRatesPath(locationId: string): string {
  return `${LOCATIONS_PATH}/${encodeURIComponent(locationId)}/tax-rates`;
}

/**
 * One `v2_tax_rate` row. `ratePercent` is a percentage (e.g. `6` = 6%), two
 * decimals. The optional `description`/`taxId`/`taxAgency` are absent when the
 * server carries none (`exactOptionalPropertyTypes`: the key is omitted).
 */
export interface TaxRate {
  id: string;
  locationId: string;
  name: string;
  /** Percentage 0–100, two decimals (DECIMAL(5,2)). */
  ratePercent: number;
  description?: string;
  taxId?: string;
  taxAgency?: string;
  isActive: boolean;
}

/**
 * Create/update body for a tax rate. `name` + `ratePercent` are required by the
 * form; the rest are optional. `isActive` is NOT settable here — activation is the
 * dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface TaxRateInput {
  name: string;
  ratePercent: number;
  description?: string;
  taxId?: string;
  taxAgency?: string;
}

/** Options for {@link listTaxRates}. */
export interface ListTaxRatesOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
}

// --- Defensive parsing helpers -------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** A boolean from a real boolean, or a fallback. Defaults to `true` (live rows). */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Assign an optional text field only when present (exactOptionalPropertyTypes). */
function assignOptionalText(
  target: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  const text = asText(value);
  if (text !== null) target[key] = text;
}

/**
 * Parse one raw tax rate, or null when it has no usable `id`. A missing
 * `name`/`ratePercent` degrades to `''`/`0` (the row still renders; the server is
 * the authority on the real value).
 */
function parseTaxRate(raw: unknown): TaxRate | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const acc: Record<string, unknown> = {};
  assignOptionalText(acc, 'description', raw['description']);
  assignOptionalText(acc, 'taxId', raw['taxId']);
  assignOptionalText(acc, 'taxAgency', raw['taxAgency']);
  return {
    id,
    // Nested v2 fk id (owning location) — numeric on the wire (DNKADM-0064).
    locationId: asId(raw['locationId']) ?? '',
    name: asText(raw['name']) ?? '',
    ratePercent: asNumber(raw['ratePercent']) ?? 0,
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    ...(acc as Partial<TaxRate>),
  };
}

/** Tolerantly extract a tax-rates array (bare array or `{ taxRates | content: [...] }`). */
function extractTaxRates(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['taxRates'])) return body['taxRates'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/** Build the JSON body for a create/update, omitting absent optional keys. */
function toBody(input: TaxRateInput): Record<string, unknown> {
  const json: Record<string, unknown> = { name: input.name, ratePercent: input.ratePercent };
  if (input.description !== undefined) json['description'] = input.description;
  if (input.taxId !== undefined) json['taxId'] = input.taxId;
  if (input.taxAgency !== undefined) json['taxAgency'] = input.taxAgency;
  return json;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/locations/{locationId}/tax-rates?status=…` → the location's tax
 * rates (defensively parsed; a bad entry dropped). The `status` filter (§4.3)
 * defaults to `active`. Throws a typed `ApiError`.
 */
export async function listTaxRates(
  locationId: string,
  options: ListTaxRatesOptions = {},
): Promise<TaxRate[]> {
  const status = options.status ?? 'active';
  const body = await request<unknown>('GET', taxRatesPath(locationId), { query: { status } });
  return extractTaxRates(body)
    .map(parseTaxRate)
    .filter((r): r is TaxRate => r !== null);
}

/**
 * `POST /v2/admin/locations/{locationId}/tax-rates` → create a tax rate. Returns
 * the re-parsed created row. Throws a typed `ApiError` (`400` VALIDATION inline-able,
 * `409` CONFLICT on the unique `(location, name)` rule, upstream).
 */
export async function createTaxRate(
  locationId: string,
  input: TaxRateInput,
): Promise<TaxRate | null> {
  const body = await request<unknown>('POST', taxRatesPath(locationId), { json: toBody(input) });
  return parseTaxRate(body);
}

/**
 * `PUT /v2/admin/locations/{locationId}/tax-rates/{id}` → update a tax rate.
 * Returns the re-parsed updated row. Throws a typed `ApiError`.
 */
export async function updateTaxRate(
  locationId: string,
  id: string,
  input: TaxRateInput,
): Promise<TaxRate | null> {
  const body = await request<unknown>(
    'PUT',
    `${taxRatesPath(locationId)}/${encodeURIComponent(id)}`,
    { json: toBody(input) },
  );
  return parseTaxRate(body);
}

/**
 * `POST /v2/admin/locations/{locationId}/tax-rates/{id}/deactivate` → soft-deactivate
 * a tax rate (§4.3). Throws a typed `ApiError`.
 */
export async function deactivateTaxRate(locationId: string, id: string): Promise<void> {
  await request<unknown>(
    'POST',
    `${taxRatesPath(locationId)}/${encodeURIComponent(id)}/deactivate`,
  );
}

/**
 * `POST /v2/admin/locations/{locationId}/tax-rates/{id}/reactivate` → reactivate a
 * soft-deactivated tax rate (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateTaxRate(locationId: string, id: string): Promise<void> {
  await request<unknown>(
    'POST',
    `${taxRatesPath(locationId)}/${encodeURIComponent(id)}/reactivate`,
  );
}
